import { useState } from 'react'
import { FolderArchive } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { FileUploader } from '@/components/tools/file-uploader'
import { FileInfo } from '@/components/tools/file-info'
import { ProgressBar } from '@/components/tools/progress-bar'
import { ResultCard } from '@/components/tools/result-card'
import { ErrorMessage } from '@/components/tools/error-message'
import { useToolStore } from '@/stores/tool-store'
import { downloadBlob } from '@/lib/download'
import { createZip } from './lib'
import { useI18n } from '@/lib/i18n'

const MAX_FILE_MB = 512

/** ZIP compression panel: multi-file pick → archive → download. */
export function CompressPanel() {
  const [files, setFiles] = useState<File[]>([])
  const { status, progress, error, resultBlob, resultName, selectFile, start, setProgress, finish, fail, reset } =
    useToolStore()
  const { t } = useI18n()

  const handleFiles = (selected: File[]) => {
    setFiles(selected)
    selectFile()
  }

  const removeFile = (file: File) => {
    const next = files.filter((item) => item !== file)
    setFiles(next)
    if (next.length === 0) reset()
  }

  const handleCompress = async () => {
    if (files.length === 0) return
    start()
    try {
      const blob = await createZip(files, setProgress)
      finish(blob, 'cloudtools-archive.zip')
    } catch (err) {
      console.error('ZIP compression failed:', err)
      fail(t('compFailed'))
    }
  }

  if (status === 'idle') {
    return (
      <FileUploader
        accept={[]}
        maxSizeMB={MAX_FILE_MB}
        multiple
        onFilesSelect={handleFiles}
      />
    )
  }

  if (status === 'processing') {
    return (
      <div className="rounded-xl border bg-card p-6 shadow-sm">
        <div className="mb-4 flex items-center gap-2">
          <FolderArchive className="h-5 w-5 text-primary" aria-hidden />
          <p className="font-medium">{t('compCreating')}</p>
        </div>
        <ProgressBar value={progress} label={t('compProgress')} />
      </div>
    )
  }

  if (status === 'done' && resultBlob && resultName) {
    return (
      <ResultCard
        fileName={resultName}
        sizeBytes={resultBlob.size}
        onDownload={() => downloadBlob(resultBlob, resultName)}
        onReset={() => {
          setFiles([])
          reset()
        }}
      />
    )
  }

  if (status === 'error') {
    return (
      <div className="space-y-4">
        <ErrorMessage
          message={error ?? t('commonErrorDefault')}
          onRetry={files.length > 0 ? handleCompress : undefined}
        />
        <div className="space-y-2">
          {files.map((file) => (
            <FileInfo key={file.name} file={file} onRemove={() => removeFile(file)} />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium">
          {files.length === 1
            ? t('compSelectedOne', { n: files.length })
            : t('compSelectedMany', { n: files.length })}
        </p>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setFiles([])
            reset()
          }}
        >
          {t('compClear')}
        </Button>
      </div>
      <div className="space-y-2">
        {files.map((file) => (
          <FileInfo key={file.name} file={file} onRemove={() => removeFile(file)} />
        ))}
      </div>
      <Button onClick={handleCompress} className="w-full" size="lg">
        <FolderArchive className="mr-1" aria-hidden />
        {t('compAction')}
      </Button>
    </div>
  )
}
